import { yupResolver } from "@hookform/resolvers/yup";
import { useCourseGroupsOptions } from "api/hooks/useCourseGroupsOptions";
import { CourseGroupAttributeType, useCourseGroupAttributesAll } from "api/coursegroup-attributes";
import { CourseGroupListContent, DEFAULT_SECTION_MARGINS, SectionMargins } from "api/shop";
import {
  PaziaFormButton,
  PaziaInput,
  PaziaMultiSelect,
  PaziaSelect,
  PaziaTextarea,
} from "components/atoms";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { ButtonVariantSelect } from "./ButtonVariantSelect";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";

const CARD_DISPLAY_ATTRIBUTE_TYPES = [
  CourseGroupAttributeType.Boolean,
  CourseGroupAttributeType.DictionarySingleSelect,
  CourseGroupAttributeType.DictionaryMultiSelect,
];

interface CourseGroupListFormProps {
  onSubmit: (data: CourseGroupListFormValues) => void;
  onCancel: () => void;
  defaultValues?: CourseGroupListContent;
  isLoading?: boolean;
}

export const CourseGroupListForm = ({ onSubmit, onCancel, defaultValues, isLoading }: CourseGroupListFormProps) => {
  const { options: courseGroupOptions } = useCourseGroupsOptions();
  const { allAttributes } = useCourseGroupAttributesAll();

  const cardDisplayAttributes = allAttributes.filter((a) => CARD_DISPLAY_ATTRIBUTE_TYPES.includes(a.attributeType));
  const cardDisplayAttributeOptions = cardDisplayAttributes.map((a) => ({ value: String(a.id), label: a.name }));

  const methods = useForm({
    // @ts-ignore temporary ignore, we know this is correct
    resolver: yupResolver(schema),
    defaultValues: {
      [FORM_NAMES.NAME]: defaultValues?.name ?? "",
      [FORM_NAMES.DESCRIPTION]: defaultValues?.description ?? "",
      [FORM_NAMES.BUTTON_TEXT]: defaultValues?.button?.text ?? "Zobacz wszystkie",
      [FORM_NAMES.BUTTON_VARIANT]: defaultValues?.button?.variant ?? "primary",
      [FORM_NAMES.BUTTON_HREF]: defaultValues?.button?.href ?? "/courses",
      [FORM_NAMES.COURSE_GROUP_IDS]: defaultValues?.courseGroupIds?.map(String) ?? [],
      [FORM_NAMES.CARD_CUSTOMIZATION]: {
        badgeAttributeIds: defaultValues?.cardCustomization?.badgeAttributeIds?.map(String) ?? [],
        listAttributeIds: defaultValues?.cardCustomization?.listAttributeIds?.map(String) ?? [],
      },
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit } = methods;

  const handleFormSubmit = (data: FormValues) => {
    const formattedData: CourseGroupListFormValues = {
      name: data.name,
      description: data.description,
      button: {
        label: data.buttonText,
        variant: data.buttonVariant as "primary" | "secondary" | "outline",
        href: data.buttonHref,
      },
      courseGroupIds: data.courseGroupIds.map(Number),
      cardCustomization: {
        badgeAttributeIds: data.cardCustomization.badgeAttributeIds.map(Number).filter((n) => !isNaN(n)),
        listAttributeIds: data.cardCustomization.listAttributeIds.map(Number).filter((n) => !isNaN(n)),
      },
      margins: data.margins,
    };
    onSubmit(formattedData);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <PaziaInput name={FORM_NAMES.NAME} label="Nazwa sekcji" required />
        <PaziaTextarea name={FORM_NAMES.DESCRIPTION} label="Opis sekcji" />

        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Przycisk</h4>
          <div className="grid gap-3 md:grid-cols-3">
            <PaziaInput name={FORM_NAMES.BUTTON_TEXT} label="Tekst przycisku" required />
            <ButtonVariantSelect name={FORM_NAMES.BUTTON_VARIANT} required />
            <PaziaInput name={FORM_NAMES.BUTTON_HREF} label="Link (href)" required />
          </div>
        </div>

        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Grupy kursów</h4>
          <PaziaMultiSelect
            name={FORM_NAMES.COURSE_GROUP_IDS}
            label="Wybierz grupy kursów"
            required
            options={courseGroupOptions}
          />
        </div>

        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Personalizacja karty produktu</h4>
          <div className="grid gap-3 md:grid-cols-2">
            <PaziaMultiSelect
              name={`${FORM_NAMES.CARD_CUSTOMIZATION}.badgeAttributeIds`}
              label="Atrybuty dla badge'ów"
              options={cardDisplayAttributeOptions}
            />
            <PaziaMultiSelect
              name={`${FORM_NAMES.CARD_CUSTOMIZATION}.listAttributeIds`}
              label="Atrybuty dla listy"
              options={cardDisplayAttributeOptions}
            />
          </div>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Wybierz atrybuty (słownikowe lub logiczne), których wartości będą wyświetlane jako badge&apos;e i elementy
            listy na kartach produktów
          </p>
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};

const FORM_NAMES = {
  NAME: "name",
  DESCRIPTION: "description",
  BUTTON_TEXT: "buttonText",
  BUTTON_VARIANT: "buttonVariant",
  BUTTON_HREF: "buttonHref",
  COURSE_GROUP_IDS: "courseGroupIds",
  CARD_CUSTOMIZATION: "cardCustomization",
} as const;

const schema = yup
  .object({
    [FORM_NAMES.NAME]: yup.string().required("Nazwa sekcji jest wymagana"),
    [FORM_NAMES.DESCRIPTION]: yup.string().default(""),
    [FORM_NAMES.BUTTON_TEXT]: yup.string().required("Tekst przycisku jest wymagany"),
    [FORM_NAMES.BUTTON_VARIANT]: yup.string().required("Wariant jest wymagany").default("primary"),
    [FORM_NAMES.BUTTON_HREF]: yup.string().required("Link jest wymagany"),
    [FORM_NAMES.COURSE_GROUP_IDS]: yup
      .array()
      .of(yup.string().required())
      .min(1, "Wybierz przynajmniej jedną grupę kursów")
      .required(),
    [FORM_NAMES.CARD_CUSTOMIZATION]: yup.object({
      badgeAttributeIds: yup.array().of(yup.string().required()).default([]),
      listAttributeIds: yup.array().of(yup.string().required()).default([]),
    }).default({ badgeAttributeIds: [], listAttributeIds: [] }),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

type FormValues = yup.InferType<typeof schema>;

export interface CourseGroupListFormValues {
  name: string;
  description: string;
  button: {
    label: string;
    variant: "primary" | "secondary" | "outline";
    href: string;
  };
  courseGroupIds: number[];
  cardCustomization?: {
    badgeAttributeIds?: number[];
    listAttributeIds?: number[];
  };
  margins?: SectionMargins;
}
